import {
  generateBindings,
  ObservableValue,
  observableValue,
  useObservedValue,
  useSharedState,
  view,
  WritableObservableValue,
} from '@esui/core';
import { ParserFormatter } from '@esui/core/formatters';
import { spread } from '@esui/core/utils/spread.js';
import { ListItemBase } from '@material/mwc-list/mwc-list-item-base.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-select';
import { css, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

export const [Select] = view('esui-mwc-select', {
  Props: class {
    disabled = false;
    required = false;
    label?: string = undefined;
    name?: string = undefined;
    value: WritableObservableValue<any> = observableValue('');
    isValid = observableValue(false);
    formatter: ParserFormatter<any> = ParserFormatter.default;
    icon?: string = undefined;
    naturalMenuWidth = false;
    fullwidth = true;
    outlined = false;
    helper?: string = undefined;
    helperPersistent = false;
    validationMessage?: string = undefined;
    selected?: ListItemBase | null = undefined;
    items:
      | SelectItem<any>[]
      | ObservableValue<SelectItem<any>[]>
      | (() => SelectItem<any>[])
      | (() => ObservableValue<SelectItem<any>[]>) = [];
    index?: number = undefined;
    validityTransform:
      | ((
          value: string,
          nativeValidity: ValidityState
        ) => Partial<ValidityState>)
      | null = null;
    validateOnInitialRender = false;
  },

  mapBuilder:
    SelectBuilder =>
    <T>(
      label: string,
      value: WritableObservableValue<T>,
      formatter: ParserFormatter<T> = ParserFormatter.default
    ) =>
      SelectBuilder().label(label).value(value).formatter(formatter),

  template(props) {
    const value = useSharedState(props.value);
    const isValid = useSharedState(props.isValid);
    const items = useObservedValue(
      typeof props.items === 'function' ? props.items() : props.items
    );

    const computedProps = generateBindings(props, [
      'items',
      'formatter',
      'validityTransform',
    ]);

    function handleChange(e: {
      target: { value: string; checkValidity: () => boolean };
    }) {
      value.value = props.formatter.parse(e.target.value);
      isValid.value = e.target.checkValidity();
    }

    return html`
      <mwc-select
        ${spread(computedProps)}
        .value="${props.formatter.format(value.value)}"
        .validityTransform="${props.validityTransform}"
        @change="${handleChange}"
      >
        <mwc-list-item></mwc-list-item>
        ${items.map(
          ({ value, name, disabled }) => html`
            <mwc-list-item
              ?disabled="${ifDefined(disabled)}"
              value="${props.formatter.format(value)}"
            >
              ${disabled ? html` <div>${name}</div>` : html`${name}`}
            </mwc-list-item>
          `
        )}
      </mwc-select>
    `;
  },
  cssTemplate: css`
    :host {
      display: inline;
    }

    mwc-select {
      width: 100%;
    }
  `,
});

export interface SelectItem<T> {
  name: string;
  value: T;
  disabled?: boolean;
}
